import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import Print from '@mui/icons-material/Print';
import Refresh from '@mui/icons-material/Refresh';
import type {
  WeeklyClassSheetDay,
  WeeklyClassSheetMetadata,
} from '../../types/WeeklyClassSheet.type';
import './WeeklyClassSheetPreview.css';

interface WeeklyClassSheetPreviewProps {
  days: WeeklyClassSheetDay[];
  metadata: WeeklyClassSheetMetadata | null;
  loading: boolean;
  error: string | null;
  subtype: string;
  onSubtypeChange?: (value: string) => void;
  onRetry: () => void;
}

const subtypeOptions = [
  { value: 'All', label: 'All' },
  { value: 'SOF', label: 'SOF' },
  { value: 'FLYING FISH', label: 'FF' },
];

const formatDayLabel = (date: string): string =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Bangkok',
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
    .format(new Date(`${date}T12:00:00.000+07:00`))
    .toUpperCase();

const levelBackground = (level: string | null): string => {
  const normalized = (level || '').toLowerCase();
  if (normalized.includes('starfish')) return '#ffe3f1';
  if (normalized.includes('seahorse')) return '#fffacc';
  if (normalized.includes('seal')) return '#e2fbd0';
  if (normalized.includes('barracuda')) return '#d6f0ff';
  if (normalized.includes('swordfish')) return '#ffe0dc';
  if (normalized.includes('dolphin')) return '#fbd4e6';
  if (normalized.includes('shark')) return '#e5e7eb';
  return '#f2f2f2';
};

const formatTime = (startTime: string | null, endTime: string | null) =>
  startTime && endTime ? `${startTime} - ${endTime}` : 'Time not set';

const printWeeklySheet = () => {
  document.body.classList.add('printing-weekly-sheet');
  const cleanup = () => {
    document.body.classList.remove('printing-weekly-sheet');
  };

  window.addEventListener('afterprint', cleanup, { once: true });
  window.print();
};

const WeeklyClassSheetPreview = ({
  days,
  metadata,
  loading,
  error,
  subtype,
  onSubtypeChange,
  onRetry,
}: WeeklyClassSheetPreviewProps) => (
  <Box className="weekly-sheet-preview" data-testid="weekly-class-sheet">
    <Box className="weekly-sheet-toolbar">
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {subtypeOptions.map((option) => (
          <Button
            key={option.value}
            size="small"
            variant={subtype === option.value ? 'contained' : 'outlined'}
            onClick={() => onSubtypeChange?.(option.value)}
            disabled={!onSubtypeChange}
          >
            {option.label}
          </Button>
        ))}
      </Stack>
      <Button
        size="small"
        variant="outlined"
        startIcon={<Print fontSize="small" />}
        onClick={printWeeklySheet}
      >
        Print
      </Button>
    </Box>

    {metadata && (
      <Typography className="weekly-sheet-summary" variant="caption">
        {metadata.from} to {metadata.toExclusive} · {metadata.totalItems}{' '}
        class(es)
      </Typography>
    )}

    {loading && (
      <Box className="weekly-sheet-state">
        <CircularProgress size={28} aria-label="Loading weekly class sheet" />
      </Box>
    )}

    {error && !loading && (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={<Refresh />}
            onClick={onRetry}
          >
            Retry
          </Button>
        }
      >
        Unable to load weekly class sheet.
      </Alert>
    )}

    {!loading && !error && (
      <Box className="weekly-sheet-days">
        {days.map((day) => (
          <section className="weekly-sheet-day" key={day.date}>
            <h3>{formatDayLabel(day.date)}</h3>
            {day.blocks.length === 0 ? (
              <div className="weekly-sheet-empty">No classes</div>
            ) : (
              <div className="weekly-sheet-blocks">
                {day.blocks.map((block) => {
                  const students = block.students.length
                    ? block.students
                    : [
                        {
                          id: -1,
                          name: 'No students',
                          age: 0,
                          level: block.level,
                        },
                      ];

                  return (
                    <article
                      className="weekly-sheet-block"
                      key={`${day.date}-${block.classId}`}
                    >
                      <div className="weekly-sheet-block-title">
                        <strong>{block.className}</strong>
                        <span>Subtype: {block.subtypeName || '---'}</span>
                        <span>
                          Coach: {block.coachNames.join(', ') || '---'}
                        </span>
                      </div>
                      <table>
                        <thead>
                          <tr>
                            <th>TIME</th>
                            <th>STUDENTS NAME</th>
                            <th>AGE</th>
                            <th>LEVEL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.map((student, index) => (
                            <tr key={`${block.classId}-${student.id}-${index}`}>
                              {index === 0 && (
                                <td
                                  className="weekly-sheet-time-cell"
                                  rowSpan={students.length}
                                >
                                  {formatTime(block.startTime, block.endTime)}
                                </td>
                              )}
                              <td className="weekly-sheet-student-cell">
                                {student.name}
                              </td>
                              <td className="weekly-sheet-age-cell">
                                {block.students.length ? student.age : '-'}
                              </td>
                              <td
                                className="weekly-sheet-level-cell"
                                style={{
                                  backgroundColor: levelBackground(
                                    student.level || block.level,
                                  ),
                                }}
                              >
                                {student.level || block.level || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        ))}
      </Box>
    )}
  </Box>
);

export default WeeklyClassSheetPreview;
